import "./ZoomableImage.css";
import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { useTracking } from "../../Tracking";

function getScrollBarWidth() {
  const parent = document.createElement("div");
  const child = document.createElement("div");
  parent.style.visibility = "hidden";
  parent.style.width = "100px";
  parent.style.position = "fixed";
  document.body.appendChild(parent);
  const regularWidth = parent.offsetWidth;
  parent.style.overflow = "scroll";
  parent.appendChild(child);
  const widthWithScrollBar = child.offsetWidth;

  parent.parentNode.removeChild(parent);

  return regularWidth - widthWithScrollBar;
}

export const ZoomableScrollImage = ({
  className = null,
  onClick = null,
  alt,
  src,
  onLoad = null,
  onOpenEvent = null,
  ...props
}) => {
  const [isMagnified, setIsMagnified] = useState(null);
  const [transform, setTransform] = useState(null);
  const [savedTransform, saveTransform] = useState(null);
  const [scrollTop, setScrollTop] = useState(0);
  const imgEl = useRef();
  const canvasEl = useRef();

  function transformString({ translateX, translateY, scaleX, scaleY }) {
    return `translateX(${translateX}px) translateY(${translateY}px) scaleX(${scaleX}) scaleY(${scaleY})`;
  }

  const zoomIn = () => {
    const PADDING = window.innerWidth <= 720 ? 4 : 12;

    const original = imgEl.current.getBoundingClientRect();

    const scrollBarWidth = getScrollBarWidth();

    const MAX_ZOOMED_WIDTH = 1200;

    const canvasHeight = window.innerHeight - PADDING * 2;
    const canvasWidth = document.body.clientWidth - PADDING * 2;

    const hasScrollBar = document.body.clientHeight > window.innerHeight;

    /*
    const desiredTargetWidth = Math.min(MAX_ZOOMED_WIDTH, desiredCanvasWidth);
    const desiredTargetHeight =
      desiredTargetWidth * (original.height / original.width);

    const willHaveScrollBar = false;//desiredTargetHeight > canvasHeight;

    const canvasWidth =
      desiredCanvasWidth - (willHaveScrollBar ? scrollBarWidth : 0);
    */

    const canvas = {
      left: PADDING,
      top: PADDING,
      width: canvasWidth,
      height: canvasHeight,
      right: PADDING + canvasWidth,
      bottom: PADDING + canvasHeight,
    };

    const targetWidth = Math.min(MAX_ZOOMED_WIDTH, canvasWidth);
    const targetHeight = targetWidth * (original.height / original.width);
    const canvasCenterX = canvas.left + canvasWidth / 2;
    const canvasCenterY = canvas.top + canvasHeight / 2;

    const targetTop = Math.max(canvas.top, canvasCenterY - targetHeight / 2);

    const target = {
      left: canvasCenterX - targetWidth / 2,
      top: targetTop,
      right: canvasCenterX + targetWidth / 2,
      bottom: targetTop + targetHeight,
    };

    const originalCenterX = original.left + original.width / 2;
    const originalCenterY = original.top + original.height / 2;
    const targetCenterX = target.left + targetWidth / 2;
    const targetCenterY = target.top + targetHeight / 2;

    const transformObject = {
      scaleX: original.width / targetWidth,
      scaleY: original.height / targetHeight,
      translateX: originalCenterX - targetCenterX,
      translateY: originalCenterY - targetCenterY + scrollTop,
    };

    setTransform(transformString(transformObject));
    saveTransform(transformObject);
    setIsMagnified(true);

    document.body.style.overflow = "hidden";
    document.body.style.marginRight = hasScrollBar ? `${scrollBarWidth}px` : 0;
  };

  const tracking = useTracking();

  useEffect(() => {
    if (onOpenEvent && isMagnified) tracking.event(onOpenEvent);
  }, [tracking, onOpenEvent, isMagnified]);

  useEffect(() => {
    if (isMagnified) {
      setTimeout(() => {
        setTransform(null);
      });
    }
  }, [isMagnified]);

  const zoomOut = () => {
    setTransform(
      transformString({
        ...savedTransform,
        translateY:
          savedTransform.translateY - scrollTop + canvasEl.current.scrollTop,
      })
    );
    setScrollTop(canvasEl.current.scrollTop);
    setTimeout(() => {
      setIsMagnified(false);
      document.body.style.overflow = "";
      document.body.style.marginRight = 0;
    }, 150);
  };

  return (
    <React.Fragment>
      <img
        data-hj-suppress
        className={`${className} ZoomableScrollImage`}
        ref={imgEl}
        onLoad={onLoad}
        onClick={function () {
          zoomIn();

          if (onClick) {
            onClick.call(this, ...arguments);
          }
        }}
        alt={alt || ""}
        src={src}
        {...props}
      />
      {ReactDOM.createPortal(
        <div
          className={`ZoomableScrollImage-container ${
            isMagnified ? "magnified" : ""
          }`}
          onClick={zoomOut}
        >
          <div className="ZoomableScrollImage-canvas" ref={canvasEl}>
            <img
              data-hj-suppress
              className="ZoomableScrollImage-magnified"
              alt=""
              src={src}
              style={{
                transform,
                transition: "transform .15s ease",
                transformOrigin: "center",
              }}
            />
          </div>
        </div>,
        document.body
      )}
    </React.Fragment>
  );
};
